
import * as db from './supabase-service';
import { askAI } from './ai-service';

export interface ReportData {
  instance_name: string;
  president_name: string;
  period: string; // e.g. "MARZO 2026"
  month: number;
  year: number;
  indicators: any[];
  executive_summary: string;
  observations: string;
  anomalies: string[];
  recommendations: string[];
  performance_total: number;
  comparison_chart_data: { period: string; meta: number; real: number }[];
}

/**
 * Reporting Service - Generador Automático de Reportes IA
 * v1.0.0
 */

// Helper to normalize months for display
const MONTH_NAMES = [
  'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
  'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'
];

/**
 * Fetches all necessary data for a specific board's report
 */
export const fetchBoardReportData = async (instanceId: string, monthNum: number, yearNum: number) => {
  // 1. Get Instance Info
  const { data: instance } = await db.supabase.from('gov_commissions').select('*').eq('id', instanceId).single();
  if (!instance) throw new Error('Instancia no encontrada');

  // 2. Get Indicators
  const indicators = await db.getIndicators(instanceId);
  
  // 3. Filter and process indicators for the specific month
  const processedIndicators = indicators.map(ind => {
    const monthData = ind.history?.find((h: any) => h.period === getShortMonth(monthNum)) || { meta: 0, real: 0 };
    const meta = monthData.meta || 0;
    // v16.4.9: Resiliente a campo 'valor' (nombre DB) además de 'real' (nombre UI)
    const real = monthData.real || monthData.valor || 0;
    const cumplimiento = meta > 0 ? Math.round((real / meta) * 100) : 0;
    
    return {
      ...ind,
      meta_mes: meta,
      real_mes: real,
      cumplimiento,
      status: cumplimiento >= 90 ? 'SATISFACTORIO' : cumplimiento >= 70 ? 'PRECAUCIÓN' : 'CRÍTICO'
    };
  });

  // 4. Calculate overall performance
  const totalWeight = processedIndicators.reduce((acc, ind) => acc + (Number(ind.peso) || 0), 0);
  const weightedPerformance = processedIndicators.reduce((acc, ind) => {
    const weight = (Number(ind.peso) || 0) / (totalWeight || 1);
    return acc + (ind.cumplimiento * weight);
  }, 0);

  // 5. Prepare comparison data (Last 3 months)
  const chartData = MONTH_NAMES.slice(Math.max(0, monthNum - 3), monthNum).map((m, idx) => {
    const mNum = monthNum - (2 - idx);
    const mShort = getShortMonth(mNum);
    
    const totals = processedIndicators.reduce((acc, ind) => {
      const h = ind.history?.find((h: any) => h.period === mShort) || { meta: 0, real: 0 };
      acc.meta += h.meta || 0;
      // v16.4.9: Resiliente a campo 'valor'
      acc.real += h.real || h.valor || 0;
      return acc;
    }, { meta: 0, real: 0 });

    return {
      period: mShort,
      meta: totals.meta,
      real: totals.real
    };
  });

  return {
    instance,
    indicators: processedIndicators,
    performance_total: Math.round(weightedPerformance),
    chart_data: chartData
  };
};

/**
 * Generates AI Narrative for the report
 */
export const generateReportNarrative = async (
  instanceName: string, 
  monthName: string, 
  indicators: any[], 
  performance: number
) => {
  const compactData = indicators.map(i => 
    `- ${i.nombre}: Meta: ${i.meta_mes} | Real: ${i.real_mes} (${i.cumplimiento}%) [${i.status}]`
  ).join('\n');

  const prompt = `Actúa como el Director Estratégico de COPARMEX. Genera el texto para el "Informe Estratégico Mensual" de la instancia "${instanceName}" para el mes de ${monthName} 2026.
  
  REGLAS DE RESPUESTA:
  - Tono ejecutivo, profesional y orientado a resultados.
  - Usa español de México.
  - El cumplimiento global es del ${performance}%.
  
  ESTRUCTURA OBLIGATORIA (Devuelve exactamente este formato):
  
  [RESUMEN_EJECUTIVO]
  (2-3 párrafos resumiendo el desempeño general, mencionando si se cumplieron objetivos clave y la tendencia observada)
  
  [OBSERVACIONES_MES]
  (3-4 puntos específicos sobre lo que sucedió este mes, anomalías detectadas y varianzas)
  
  [RECOMENDACIONES]
  (3 recomendaciones accionables para mejorar el desempeño el próximo mes)
  `;

  const context = `Indicadores del mes:\n${compactData}\nCumplimiento Total: ${performance}%`;
  
  const aiResponse = await askAI(prompt, context);
  
  // Parse AI response
  const summaryPart = aiResponse.split('[RESUMEN_EJECUTIVO]')[1]?.split('[OBSERVACIONES_MES]')[0]?.trim() || '';
  const observationsPart = aiResponse.split('[OBSERVACIONES_MES]')[1]?.split('[RECOMENDACIONES]')[0]?.trim() || '';
  const recommendationsPart = aiResponse.split('[RECOMENDACIONES]')[1]?.trim() || '';

  return {
    summary: summaryPart,
    observations: observationsPart,
    recommendations: recommendationsPart
  };
};

/**
 * Gets short month name (ENE, FEB, etc)
 */
const getShortMonth = (m: number) => {
  const months = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
  return months[m - 1];
};

export default {
  fetchBoardReportData,
  generateReportNarrative
};
